import React from "react";
import PropTypes from "prop-types";

import withStyles from "@material-ui/core/styles/withStyles";
import withTheme from "@material-ui/core/styles/withTheme";
import FirstPage from "@material-ui/icons/FirstPage";
import IconButton from "@material-ui/core/IconButton";
import LastPage from "@material-ui/icons/LastPage";
import NavigateBefore from "@material-ui/icons/NavigateBefore";
import NavigateNext from "@material-ui/icons/NavigateNext";
import Typography from "@material-ui/core/Typography";

const styles = theme => ({
  container: {
    display: "flex",
    alignItems: "center"
  },
  pageNumber: {
    color: theme.palette.links,
    border: "none",
    cursor: "pointer",
    background: "transparent"
  },
  pageNumberActive: {
    color: theme.palette.secondary.main,
    margin: theme.spacing(0.5)
  },
  iconButtonRoot: {
    padding: 6
  }
});

const OSSTablePaginationActions = ({
  backIconButtonProps,
  classes,
  count,
  nextIconButtonProps,
  onChangePage,
  page,
  rowsPerPage
}) => {
  const handleFirstPageButtonClick = event => {
    onChangePage(event, 0);
  };

  const handleBackButtonClick = event => {
    onChangePage(event, page - 1);
  };

  const handleNextButtonClick = event => {
    onChangePage(event, page + 1);
  };

  const handleLastPageButtonClick = event => {
    onChangePage(event, Math.max(0, Math.ceil(count / rowsPerPage) - 1));
  };

  const handlePageNumberClick = (event, pageNumber) => {
    onChangePage(event, pageNumber);
  };

  const generatePageRange = (numberOfPages, currentPage) => {
    let pageRange = [];
    let pageSpread = 5; // Number of pages shown on either side of the current page

    let startPage;
    let endPage;

    // Determine if we can show the pageSpread _before_ the currentPage.
    if (currentPage - pageSpread < 1) {
      startPage = 0;
    } else {
      startPage = currentPage - pageSpread;
    }
    // Determine if we can show the pageSpread _after_ the currentPage.
    if (currentPage + pageSpread > numberOfPages) {
      endPage = numberOfPages;
    } else {
      endPage = currentPage + pageSpread;
    }

    // If there is no pageSpread _before_ the current page,
    // double the pageSpread _after_ the currentPage instead.
    if (startPage === 0) {
      if (startPage + pageSpread * 2 > numberOfPages) {
        endPage = numberOfPages;
      } else {
        endPage = startPage + pageSpread * 2;
      }
    }

    for (
      let pageNumberIndex = startPage;
      pageNumberIndex < endPage;
      pageNumberIndex++
    ) {
      pageRange.push(pageNumberIndex);
    }

    return pageRange;
  };

  const { container, pageNumber, pageNumberActive } = classes;
  const numberOfPages = Math.ceil(count / rowsPerPage);
  const pageRange = generatePageRange(numberOfPages, page);

  if (numberOfPages <= 1) {
    return null;
  }

  return (
    <div className={container}>
      <IconButton
        classes={{ root: classes.iconButtonRoot }}
        onClick={handleFirstPageButtonClick}
        disabled={page === 0}
        aria-label="First Page"
      >
        <FirstPage />
      </IconButton>
      <IconButton
        classes={{ root: classes.iconButtonRoot }}
        onClick={handleBackButtonClick}
        disabled={page === 0}
        {...backIconButtonProps}
      >
        <NavigateBefore />
      </IconButton>

      {pageRange.map((number, index) => (
        <Typography
          variant="caption"
          key={index}
          data-testid={`pagination-number-${index}`}
        >
          {page === number ? (
            <span className={pageNumberActive}>{number + 1}</span>
          ) : (
            <button
              className={pageNumber}
              onClick={event => handlePageNumberClick(event, number)}
            >
              {number + 1}
            </button>
          )}
        </Typography>
      ))}

      <IconButton
        classes={{ root: classes.iconButtonRoot }}
        onClick={handleNextButtonClick}
        disabled={page >= numberOfPages - 1}
        {...nextIconButtonProps}
      >
        <NavigateNext />
      </IconButton>
      <IconButton
        classes={{ root: classes.iconButtonRoot }}
        onClick={handleLastPageButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label="Last Page"
      >
        <LastPage />
      </IconButton>
    </div>
  );
};

OSSTablePaginationActions.propTypes = {
  backIconButtonProps: PropTypes.object,
  classes: PropTypes.object.isRequired,
  nextIconButtonProps: PropTypes.object,
  onChangePage: PropTypes.func.isRequired,
  rowsPerPage: PropTypes.number.isRequired
};

export default withStyles(styles)(withTheme(OSSTablePaginationActions));
